/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback} from 'react';
import isEmpty from 'lodash.isempty';
import isNull from 'lodash.isnull';
import {Account} from '@ion/core/src/types/entities';
import {UseGroupAssignmentDetailsFormResult} from '@ion/dls/src/hooks/useGroupAssignmentDetailsForm';
import AddGroupAssignmentAction from './AddGroupAssignmentAction';
import {
  GroupAssignmentDetailsFields,
  isRemove
} from './GroupAssignmentDetailsForm';
import NewGroupAssignmentAction from './NewGroupAssignmentAction';
import RemoveGroupAssignmentAction from './RemoveGroupAssignmentAction';
import ReplaceGroupAssignmentAction from './ReplaceGroupAssignmentAction';
import {GroupAssignmentActions} from './types';
import {UseGroupAssignmentChooserResult} from './useGroupAssignmentChooser';

export interface GroupAssignmentAccountChooserProps extends
  UseGroupAssignmentChooserResult,
  UseGroupAssignmentDetailsFormResult {
  account?: Account;
  isTotalRowsSelected?: boolean;
  isSelectedGridList?: boolean;
  path?: string;
  multiSelect?: boolean;
}

/**
 The GroupAssignmentActionChooser provides a list of actions for display within a
 * dropdown list allowing the user to select a action from the list.
 */
export const GroupAssignmentAccountChooser = ({
  groups,
  groupsResult,
  groupAction,
  account,
  replaceGroups,
  handleRemoveGroupChooser,
  handleFromGroupChooser,
  handleAccountGroupChooser,
  handleGroupChooser,
  handleGroupNameChooser,
  handleGroupNotesChooser,
  areGroupsLoading,
  fromGroup,
  toGroup,
  groupName,
  groupNotes,
  formState,
  fromAccount,
  multiSelect = true,
  isNewGroup = false,
  isGroupNameEmpty = false,
  isGroupNameExists = false,
  isTotalRowsSelected = false,
  isSelectedGridList = false,
  isFromGroupEmpty = false
}: GroupAssignmentAccountChooserProps) => {
  const path            = 'common:editors.bulkActions.groupAssignment';
  const showAddNewGroup = isNewGroup || isEmpty(groups);
  const isAddGroup      = formState[
    GroupAssignmentDetailsFields.ADD_GROUP
  ]?.value?.name === GroupAssignmentDetailsFields.ADD_NEW_GROUP;
  const isReplaceGroup  = formState[
    GroupAssignmentDetailsFields.REPLACE_TO_GROUP
  ]?.value?.name === GroupAssignmentDetailsFields.ADD_NEW_GROUP;
  const showNewGroup    = isAddGroup || isReplaceGroup;

  const showNewGroupAssignment = useCallback((): boolean => {
    if (!isNewGroup) {
      return false;
    } if (isNewGroup && isSelectedGridList) {
      return showAddNewGroup;
    } if (showNewGroup) {
      return showNewGroup;
    }
  }, [isNewGroup, isSelectedGridList, showNewGroup]);

  return (
    <>
      {groupAction === GroupAssignmentActions.Remove && (
        <RemoveGroupAssignmentAction multiSelect={multiSelect}
          replaceGroups={isTotalRowsSelected ? groupsResult : replaceGroups}
          areGroupsLoading={areGroupsLoading}
          handleRemoveGroupChooser={handleRemoveGroupChooser}
          formState={formState}
          path={path} />
      )}
      {groupAction === GroupAssignmentActions.Replace && (
        <ReplaceGroupAssignmentAction replaceGroups={isTotalRowsSelected ? groupsResult : replaceGroups}
          areGroupsLoading={areGroupsLoading}
          handleFromGroupChooser={handleFromGroupChooser}
          toGroup={toGroup}
          fromAccount={fromAccount}
          formState={formState}
          path={path}
          isFromGroupEmpty={isFromGroupEmpty} />
      )}
      {groupAction !== GroupAssignmentActions.Remove && (
        <AddGroupAssignmentAction groupAction={groupAction}
          multiSelect={groupAction !== GroupAssignmentActions.Replace}
          account={account}
          handleAccountGroupChooser={handleAccountGroupChooser}
          handleGroupChooser={handleGroupChooser}
          allGroups={groups}
          areGroupsLoading={areGroupsLoading}
          isNewGroup={isNewGroup}
          fromGroup={fromGroup}
          toGroup={toGroup}
          isSelectedGridList={isSelectedGridList}
          formState={formState}
          path={path} />
      )}
      {showNewGroupAssignment() && (
        <NewGroupAssignmentAction isGroupNameEmpty={isGroupNameEmpty}
          isGroupNameExists={isGroupNameExists}
          areGroupsLoading={areGroupsLoading}
          groupName={groupName}
          groupNotes={groupNotes}
          path={path}
          handleGroupNameChooser={handleGroupNameChooser}
          handleGroupNotesChooser={handleGroupNotesChooser} />
      )}
    </>
  );
};

export default GroupAssignmentAccountChooser;
